/**
 * The sample estate for Asset Management.
 *
 * A small, coherent Madinah register — airfield, terminal plant, electrical
 * and landside — with install dates, makers, costs, a few components, open
 * defects, corrective history and preventive plans, so every tab of the suite
 * has something true to say in the demonstration. Every view is computed from
 * these records with the same arithmetic the platform uses
 * (`domain/assetManagement.ts`), not written out by hand: change an install
 * date and the forecast moves.
 *
 * Deterministic: the same records every time the page opens, placed relative
 * to the sample clock so "due in 12 days" stays true on any day.
 */
import {
  bandOf,
  health,
  lifeOf,
  planState,
  renewalForecast,
  riskOf,
  type AmAsset,
  type AmAssetDetail,
  type AmAssetRow,
  type AmCondition,
  type AmFilter,
  type AmImportResult,
  type AmImportRow,
  type AmLifecycle,
  type AmMaintenance,
  type AmPatch,
  type AmPlan,
  type AmPlanInput,
  type AmPolicy,
  type AmRegister,
  type AmZone,
  type AmTypeDef,
  type AmCreateInput,
  type AmHistoryEvent,
  type AmNote,
  type AttrSchema,
  type LifecycleStage,
  type NoteCategory,
} from "../domain/assetManagement";
import {
  conditionRefusal,
  envelopeRefusal,
  planPatchRefusal,
  type AmConditionInput,
  type AmConditionResult,
  type AmSurvey,
  type EnvelopeInput,
  type FundingEnvelope,
} from "../domain/entryForms";
import { DataSourceError } from "../api/dataSource";
import { TODAY } from "./clock";

const DAY = 86_400_000;
const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysFrom = (n: number) => new Date(TODAY.getTime() + n * DAY);

interface TypeDef {
  key: string;
  name: string;
  category: string;
  prefix: string;
  zone: string;
  count: number;
  maker: string[];
  cost: number;
  life: number;
  criticality: number;
}

const TYPES: TypeDef[] = [
  {
    key: "agl_light",
    name: "AGL light",
    category: "airfield lighting",
    prefix: "AGL-RWY17",
    zone: "RWY-17-35",
    count: 24,
    maker: ["ADB Safegate", "Honeywell"],
    cost: 2600,
    life: 12,
    criticality: 4,
  },
  {
    key: "ccr",
    name: "Constant current regulator",
    category: "airfield lighting",
    prefix: "CCR",
    zone: "AGL-VAULT",
    count: 6,
    maker: ["ADB Safegate"],
    cost: 42000,
    life: 20,
    criticality: 5,
  },
  {
    key: "pavement_section",
    name: "Pavement section",
    category: "civil",
    prefix: "PAV-TWY-A",
    zone: "TWY-A",
    count: 8,
    maker: [],
    cost: 380000,
    life: 20,
    criticality: 4,
  },
  {
    key: "surface_marking",
    name: "Surface marking",
    category: "civil",
    prefix: "MRK-APR",
    zone: "APRON-1",
    count: 8,
    maker: [],
    cost: 9000,
    life: 3,
    criticality: 3,
  },
  {
    key: "chiller",
    name: "Chiller",
    category: "terminal plant",
    prefix: "CHL",
    zone: "TERM-PLANT",
    count: 4,
    maker: ["Carrier", "Trane"],
    cost: 620000,
    life: 20,
    criticality: 5,
  },
  {
    key: "ahu",
    name: "Air handling unit",
    category: "terminal plant",
    prefix: "AHU",
    zone: "TERM-L1",
    count: 10,
    maker: ["Trox", "Daikin"],
    cost: 145000,
    life: 20,
    criticality: 3,
  },
  {
    key: "pump",
    name: "Pump",
    category: "terminal plant",
    prefix: "PMP",
    zone: "TERM-PLANT",
    count: 8,
    maker: ["Grundfos", "KSB"],
    cost: 38000,
    life: 15,
    criticality: 3,
  },
  {
    key: "escalator",
    name: "Escalator",
    category: "terminal plant",
    prefix: "ESC",
    zone: "TERM-L1",
    count: 6,
    maker: ["Schindler", "KONE"],
    cost: 510000,
    life: 25,
    criticality: 4,
  },
  {
    key: "elevator",
    name: "Elevator",
    category: "terminal plant",
    prefix: "ELV",
    zone: "TERM-L1",
    count: 6,
    maker: ["Otis", "KONE"],
    cost: 430000,
    life: 25,
    criticality: 4,
  },
  {
    key: "pbb",
    name: "Passenger boarding bridge",
    category: "terminal plant",
    prefix: "PBB",
    zone: "APRON-1",
    count: 5,
    maker: ["TK Airport Solutions", "CIMC"],
    cost: 2400000,
    life: 25,
    criticality: 5,
  },
  {
    key: "bhs_conveyor",
    name: "Baggage conveyor",
    category: "terminal plant",
    prefix: "BHS",
    zone: "TERM-BHS",
    count: 8,
    maker: ["Vanderlande"],
    cost: 185000,
    life: 20,
    criticality: 4,
  },
  {
    key: "transformer",
    name: "Transformer",
    category: "electrical",
    prefix: "TX",
    zone: "SUBSTN",
    count: 5,
    maker: ["ABB", "Siemens"],
    cost: 350000,
    life: 30,
    criticality: 5,
  },
  {
    key: "generator",
    name: "Standby generator",
    category: "electrical",
    prefix: "GEN",
    zone: "SUBSTN",
    count: 3,
    maker: ["Caterpillar", "Cummins"],
    cost: 780000,
    life: 25,
    criticality: 5,
  },
  {
    key: "ups",
    name: "UPS",
    category: "electrical",
    prefix: "UPS",
    zone: "SUBSTN",
    count: 4,
    maker: ["Eaton", "Schneider"],
    cost: 95000,
    life: 12,
    criticality: 4,
  },
  {
    key: "cctv_camera",
    name: "CCTV camera",
    category: "security",
    prefix: "CAM",
    zone: "TERM-L1",
    count: 10,
    maker: ["Axis", "Bosch"],
    cost: 6500,
    life: 8,
    criticality: 2,
  },
  {
    key: "road_pavement",
    name: "Road pavement",
    category: "landside",
    prefix: "RD",
    zone: "LANDSIDE",
    count: 4,
    maker: [],
    cost: 900000,
    life: 20,
    criticality: 2,
  },
];

const ZONES: AmZone[] = [
  { id: "z-airside", parentId: null, code: "AIRSIDE", name: "Airside", level: 1, zoneType: "area", assets: 0 },
  {
    id: "z-rwy",
    parentId: "z-airside",
    code: "RWY-17-35",
    name: "Runway 17/35",
    level: 2,
    zoneType: "runway",
    assets: 0,
  },
  { id: "z-twy", parentId: "z-airside", code: "TWY-A", name: "Taxiway A", level: 2, zoneType: "taxiway", assets: 0 },
  { id: "z-apron", parentId: "z-airside", code: "APRON-1", name: "Apron 1", level: 2, zoneType: "apron", assets: 0 },
  {
    id: "z-vault",
    parentId: "z-airside",
    code: "AGL-VAULT",
    name: "AGL vault",
    level: 2,
    zoneType: "building",
    assets: 0,
  },
  {
    id: "z-term",
    parentId: null,
    code: "TERMINAL",
    name: "Passenger terminal",
    level: 1,
    zoneType: "building",
    assets: 0,
  },
  { id: "z-l1", parentId: "z-term", code: "TERM-L1", name: "Terminal level 1", level: 2, zoneType: "level", assets: 0 },
  {
    id: "z-plant",
    parentId: "z-term",
    code: "TERM-PLANT",
    name: "Central plant room",
    level: 2,
    zoneType: "plant_room",
    assets: 0,
  },
  { id: "z-bhs", parentId: "z-term", code: "TERM-BHS", name: "Baggage hall", level: 2, zoneType: "level", assets: 0 },
  { id: "z-sub", parentId: null, code: "SUBSTN", name: "Main substation", level: 1, zoneType: "building", assets: 0 },
  { id: "z-land", parentId: null, code: "LANDSIDE", name: "Landside", level: 1, zoneType: "area", assets: 0 },
];

/** Attribute schemas for the sample types, in the shape the airports pack declares them. */
const SAMPLE_SCHEMAS: Record<string, AttrSchema> = {
  agl_light: {
    type: "object",
    required: ["light_type", "mounting"],
    properties: {
      light_type: {
        type: "string",
        enum: ["runway_edge", "runway_threshold", "runway_centreline", "taxiway_edge", "approach", "papi"],
      },
      mounting: { type: "string", enum: ["inset", "elevated"] },
      colour: { type: "string", enum: ["white", "red", "green", "blue", "yellow"] },
      lamp_type: { type: "string", enum: ["halogen", "led"] },
      rated_hours: { type: "number", minimum: 0 },
      circuit_tag: { type: "string" },
    },
  },
  chiller: { type: "object", properties: { capacity_tr: { type: "number", minimum: 0 } } },
  pump: {
    type: "object",
    properties: { flow_m3h: { type: "number", minimum: 0 }, head_m: { type: "number", minimum: 0 } },
  },
  transformer: {
    type: "object",
    properties: { rating_kva: { type: "number", minimum: 0 }, voltage_kv: { type: "number" } },
  },
  generator: {
    type: "object",
    properties: { rating_kva: { type: "number", minimum: 0 }, fuel: { type: "string", enum: ["diesel", "gas"] } },
  },
  pavement_section: {
    type: "object",
    properties: {
      surface_type: { type: "string", enum: ["asphalt", "concrete"] },
      area_m2: { type: "number", minimum: 0 },
      pcn: { type: "string" },
    },
  },
};

/** A small deterministic sequence, so the sample is the same every time. */
function seq(seed: number) {
  let x = seed;
  return () => {
    x = (x * 1103515245 + 12345) % 2147483648;
    return x / 2147483648;
  };
}

interface Stored {
  asset: AmAsset;
  zoneId: string;
  defects: number[];
  prediction: number | null;
}

interface StoredPlan extends AmPlan {
  assetId: string;
  leadDays: number;
  createdAt: string;
}

export class MockAssetManagement {
  private assets = new Map<string, Stored>();
  private plans: StoredPlan[] = [];
  private policies = new Map<string, AmPolicy>();
  private workOrders = new Map<string, AmAssetDetail["workOrders"]>();
  /* build 6B: funding envelopes as the platform lists them (the sample's three years are marked as such) */
  private envelopes: FundingEnvelope[] = [];
  private woSeq = 900;

  private siteId: string;

  constructor(siteId = "site_oema") {
    this.siteId = siteId;
    const rnd = seq(20260926);
    for (const td of TYPES) {
      const zone = ZONES.find((z) => z.code === td.zone) as AmZone;
      for (let i = 1; i <= td.count; i++) {
        const id = `am-${td.key}-${i}`;
        const tag = `${td.prefix}-${String(i).padStart(3, "0")}`;
        // a spread of ages, a few past their life, a few undated on purpose
        const undated = rnd() < 0.12;
        const ageYears = Math.round(rnd() * td.life * 1.25 * 10) / 10;
        const installed = undated ? null : iso(new Date(TODAY.getTime() - ageYears * 365.25 * DAY));
        const maker = td.maker.length ? td.maker[Math.floor(rnd() * td.maker.length)] : null;
        const surveyed = rnd() < 0.45 ? Math.round(45 + rnd() * 50) : null;
        const stage: LifecycleStage =
          ageYears > td.life * 1.1 && rnd() < 0.5 ? "degraded" : rnd() < 0.04 ? "standby" : "in_service";
        const asset: AmAsset = {
          id,
          siteId: this.siteId,
          tag,
          name: `${td.name} ${tag}`,
          assetType: td.key,
          assetTypeName: td.name,
          category: td.category,
          conditionModel: null,
          zoneCode: zone.code,
          zoneName: zone.name,
          parentId: null,
          parentTag: null,
          criticality: Math.max(1, Math.min(5, td.criticality + (rnd() < 0.2 ? -1 : 0))),
          status: "in_service",
          stage,
          conditionScore: surveyed,
          healthScore: null,
          healthBand: null,
          healthBasis: null,
          installedOn: installed,
          manufacturer: maker,
          model: maker ? `${maker.split(" ")[0].toUpperCase()}-${100 + Math.floor(rnd() * 800)}` : null,
          serialNo: maker ? `SN${Math.floor(rnd() * 9e6 + 1e6)}` : null,
          purchasedOn: installed,
          purchaseCost: rnd() < 0.5 ? Math.round(td.cost * (0.8 + rnd() * 0.3)) : null,
          replacementValue: rnd() < 0.35 ? Math.round(td.cost * (1 + rnd() * 0.25)) : null,
          designLifeYears: null,
          warrantyUntil:
            ageYears < 2.5 ? iso(new Date(new Date(installed ?? iso(TODAY)).getTime() + 2 * 365.25 * DAY)) : null,
          disposedOn: null,
          depreciationMethod: "straight_line",
          residualPct: 0,
          attrs: {},
        };
        const defects: number[] = [];
        if (rnd() < 0.18) defects.push(1 + Math.floor(rnd() * 5));
        if (rnd() < 0.06) defects.push(3 + Math.floor(rnd() * 3));
        this.assets.set(id, {
          asset,
          zoneId: zone.id,
          defects,
          prediction: rnd() < 0.15 ? Math.round(rnd() * 90) / 100 : null,
        });
        // corrective history on a few, most on the ones that are wearing out
        const failures = rnd() < 0.25 ? 1 + Math.floor(rnd() * (ageYears > td.life ? 5 : 2)) : 0;
        const wos: AmAssetDetail["workOrders"] = [];
        for (let f = 0; f < failures; f++) {
          const opened = daysFrom(-Math.floor(rnd() * 700) - 5);
          const hours = 2 + Math.floor(rnd() * 30);
          wos.push({
            id: `am-wo-${id}-${f}`,
            number: `WO-${String(++this.woSeq).padStart(6, "0")}`,
            kind: "corrective",
            status: "completed",
            priority: 2 + Math.floor(rnd() * 3),
            title: `Repair ${tag}`,
            openedAt: opened.toISOString(),
            completedAt: new Date(opened.getTime() + hours * 3_600_000).toISOString(),
            costActual: Math.round(td.cost * (0.01 + rnd() * 0.04)),
          });
        }
        this.workOrders.set(id, wos);
      }
    }
    // components: the pumps belong to the chillers, the UPS units to the substation's first transformer
    for (let i = 1; i <= 8; i++) this.setParent(`am-pump-${i}`, `am-chiller-${((i - 1) % 4) + 1}`);
    for (let i = 1; i <= 4; i++) this.setParent(`am-ups-${i}`, "am-transformer-1");

    // policies for the airfield and plant families; the rest left for the operator
    for (const td of TYPES.filter((t) =>
      ["agl_light", "ccr", "chiller", "ahu", "pump", "ups", "cctv_camera", "surface_marking"].includes(t.key),
    ))
      this.policies.set(td.key, {
        assetType: td.key,
        designLifeYears: td.life,
        replacementCost: td.cost,
        currency: "SAR",
        note: "Sample planning figure",
      });

    this.envelopes = [
      [TODAY.getUTCFullYear(), 2_500_000],
      [TODAY.getUTCFullYear() + 1, 3_000_000],
      [TODAY.getUTCFullYear() + 2, 3_000_000],
    ].map(([year, amount]) => ({
      id: `env-sample-${year}`,
      fiscalYear: year,
      kind: "capex" as const,
      amount,
      currency: "SAR",
      label: "Sample planning figure",
      scope: "site" as const,
      applies: true,
      setBy: null,
      setAt: null,
    }));

    // preventive plans, a few overdue, a few due, most scheduled
    const planned: [string, string, number, number][] = [
      ["chiller", "Quarterly chiller service", 90, -12],
      ["generator", "Monthly load test", 30, 4],
      ["transformer", "Annual oil analysis and thermography", 365, 40],
      ["escalator", "Monthly escalator inspection", 30, -3],
      ["elevator", "Monthly lift inspection", 30, 9],
      ["pbb", "Quarterly bridge service", 90, 21],
      ["ups", "Six-monthly battery test", 182, 60],
      ["ccr", "Annual CCR calibration", 365, 120],
    ];
    let n = 0;
    for (const [type, title, interval, firstDue] of planned) {
      for (const s of [...this.assets.values()].filter((x) => x.asset.assetType === type)) {
        n++;
        const due = daysFrom(firstDue + (n % 5) * 3);
        this.plans.push({
          id: `am-plan-${n}`,
          assetId: s.asset.id,
          tag: s.asset.tag,
          assetName: s.asset.name,
          assetType: type,
          criticality: s.asset.criticality,
          title,
          trigger: "calendar",
          intervalDays: interval,
          usageMeasurement: null,
          intervalUsage: null,
          leadDays: 14,
          priority: s.asset.criticality >= 5 ? 2 : 3,
          estimatedHours: 4,
          estimatedCost: null,
          tasks: [],
          active: true,
          lastDoneAt: new Date(due.getTime() - interval * DAY).toISOString(),
          nextDueAt: due.toISOString(),
          state: "scheduled",
          openWorkOrder: null,
          createdAt: daysFrom(-400).toISOString(),
        });
      }
    }
    this.refresh();
  }

  private setParent(child: string, parent: string) {
    const c = this.assets.get(child);
    const p = this.assets.get(parent);
    if (c && p) {
      c.asset.parentId = p.asset.id;
      c.asset.parentTag = p.asset.tag;
    }
  }

  /** Health for every asset, from the same rules as the platform's job. */
  refresh(): { assessed: number; changed: number } {
    let assessed = 0;
    let changed = 0;
    for (const s of this.assets.values()) {
      const life = s.asset.designLifeYears ?? this.policies.get(s.asset.assetType)?.designLifeYears ?? null;
      const h = health({
        conditionScore: s.asset.conditionScore,
        openDefectSeverities: s.defects,
        installedOn: s.asset.installedOn ?? s.asset.purchasedOn,
        designLifeYears: life,
        failureProbability: s.prediction,
        today: TODAY,
      });
      if (h.score !== null) assessed++;
      if (h.score !== s.asset.healthScore) changed++;
      s.asset.healthScore = h.score;
      s.asset.healthBand = h.band;
      s.asset.healthBasis = h.basis;
    }
    return { assessed, changed };
  }

  private row(s: Stored): AmAssetRow {
    const a = s.asset;
    return {
      id: a.id,
      tag: a.tag,
      name: a.name,
      assetType: a.assetType,
      assetTypeName: a.assetTypeName,
      zoneId: s.zoneId,
      zoneCode: a.zoneCode,
      parentId: a.parentId,
      criticality: a.criticality,
      status: a.status,
      stage: a.stage,
      conditionScore: a.conditionScore,
      healthScore: a.healthScore,
      healthBand: a.healthBand,
      installedOn: a.installedOn,
      manufacturer: a.manufacturer,
      model: a.model,
      serialNo: a.serialNo,
      children: [...this.assets.values()].filter((x) => x.asset.parentId === a.id).length,
    };
  }

  /** Every work order on the sample register, with its asset — what HR prices in people's time. */
  workRecords(): { wo: AmAssetDetail["workOrders"][number]; asset: AmAsset }[] {
    return [...this.workOrders].flatMap(([id, wos]) => {
      const st = this.assets.get(id);
      return st ? wos.map((wo) => ({ wo, asset: st.asset })) : [];
    });
  }

  /** The preventive plans with their asset's category, for the planned labour. */
  planRecords(): { plan: AmPlan; assetId: string; category: string | null }[] {
    return this.plans.map((p) => ({
      plan: p,
      assetId: p.assetId,
      category: this.assets.get(p.assetId)?.asset.category ?? null,
    }));
  }

  register(filter: AmFilter = {}): AmRegister {
    const all = [...this.assets.values()];
    const under = (zoneId: string): Set<string> => {
      const out = new Set([zoneId]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const z of ZONES) {
          if (z.parentId && out.has(z.parentId) && !out.has(z.id)) {
            out.add(z.id);
            grew = true;
          }
        }
      }
      return out;
    };
    const zoneSet = filter.zoneId ? under(filter.zoneId) : null;
    const q = filter.q?.toLowerCase();
    const rows = all
      .filter((s) => !q || `${s.asset.tag} ${s.asset.name} ${s.asset.serialNo ?? ""}`.toLowerCase().includes(q))
      .filter((s) => !filter.assetType || s.asset.assetType === filter.assetType)
      .filter((s) => !zoneSet || zoneSet.has(s.zoneId))
      .filter((s) => !filter.stage || s.asset.stage === filter.stage)
      .sort((a, b) => b.asset.criticality - a.asset.criticality || a.asset.tag.localeCompare(b.asset.tag))
      .map((s) => this.row(s));
    const types = TYPES.map((t) => ({
      key: t.key,
      name: t.name,
      category: t.category,
      assets: all.filter((s) => s.asset.assetType === t.key).length,
    }));
    return {
      site: { id: this.siteId, code: "OEMA", name: "Prince Mohammad Bin Abdulaziz International Airport, Madinah" },
      zones: ZONES.map((z) => ({ ...z, assets: all.filter((s) => s.zoneId === z.id).length })),
      types,
      assets: rows,
      total: rows.length,
      truncated: false,
      coverage: {
        assets: all.length,
        dated: all.filter((s) => s.asset.installedOn || s.asset.purchasedOn).length,
        assessed: all.filter((s) => s.asset.healthScore !== null).length,
        withMaker: all.filter((s) => s.asset.manufacturer).length,
        components: all.filter((s) => s.asset.parentId).length,
      },
    };
  }

  asset(id: string): AmAssetDetail {
    const s = this.assets.get(id);
    if (!s) throw new Error("asset not found");
    const zone = ZONES.find((z) => z.id === s.zoneId);
    const path = [];
    for (let z = zone; z; z = ZONES.find((x) => x.id === z?.parentId)) path.unshift({ code: z.code, name: z.name });
    const wos = this.workOrders.get(id) ?? [];
    const corrective = wos.filter((w) => w.kind === "corrective" && w.completedAt);
    const repair = corrective.map(
      (w) => (new Date(w.completedAt as string).getTime() - new Date(w.openedAt).getTime()) / 3_600_000,
    );
    const now = TODAY;
    const policy = this.policies.get(s.asset.assetType) ?? null;
    return {
      asset: structuredClone(s.asset),
      zonePath: path,
      children: [...this.assets.values()]
        .filter((x) => x.asset.parentId === id)
        .map((x) => ({
          id: x.asset.id,
          tag: x.asset.tag,
          name: x.asset.name,
          assetType: x.asset.assetType,
          healthBand: x.asset.healthBand,
          stage: x.asset.stage,
        })),
      workOrders: [...wos].sort((a, b) => b.openedAt.localeCompare(a.openedAt)),
      defects: s.defects.map((sev, i) => ({
        id: `${id}-d${i}`,
        defectType: null,
        severity: sev,
        status: "open",
        summary: sev >= 4 ? "Serious finding awaiting repair" : "Minor finding recorded on inspection",
        detectedAt: daysFrom(-10 - i * 7).toISOString(),
      })),
      readings: [],
      predictions:
        s.prediction !== null
          ? [
              {
                modelKey: "rotating_plant_health",
                horizonDays: 90,
                predictedValue: null,
                probability: s.prediction,
                confidence: 0.6,
                rulP50Days: Math.round((1 - s.prediction) * 400),
                predictedAt: daysFrom(-2).toISOString(),
              },
            ]
          : [],
      documents: [],
      plans: this.plans.filter((p) => p.assetId === id).map((p) => ({ ...p, state: planState(p, now) })),
      healthHistory: s.asset.healthScore !== null ? this.history(s.asset.healthScore) : [],
      life: lifeOf(s.asset, policy, TODAY),
      policy,
      reliability: {
        failures: corrective.length,
        mtbfDays: corrective.length ? Math.round((730 / corrective.length) * 10) / 10 : null,
        mttrHours: repair.length ? Math.round((repair.reduce((a, b) => a + b, 0) / repair.length) * 10) / 10 : null,
      },
      costToDate: wos.reduce((sum, w) => sum + (w.costActual ?? 0), 0),
    };
  }

  /** Six months of gently falling health ending at today's score, for the sparkline. */
  private history(score: number) {
    return Array.from({ length: 6 }, (_, i) => {
      const s = Math.min(100, Math.round((score + (5 - i) * 1.6) * 10) / 10);
      return { observedOn: iso(daysFrom(-(5 - i) * 30)), score: s, band: bandOf(s) };
    });
  }

  edit(id: string, patch: AmPatch, who = "You"): { changed: string[] } {
    const s = this.assets.get(id);
    if (!s) throw new Error("asset not found");
    if (patch.parentId) {
      if (patch.parentId === id) throw new Error("an asset cannot be its own parent");
      for (
        let p = this.assets.get(patch.parentId);
        p;
        p = p.asset.parentId ? this.assets.get(p.asset.parentId) : undefined
      )
        if (p.asset.id === id) throw new Error("that parent is already a component of this asset");
    }
    const changed: string[] = [];
    const changes: { field: string; before: unknown; after: unknown }[] = [];
    const a = s.asset as unknown as Record<string, unknown>;
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) continue;
      if (k === "attrs") {
        for (const [ak, av] of Object.entries(v as Record<string, unknown>)) {
          const before = s.asset.attrs[ak];
          if (av === null) delete s.asset.attrs[ak];
          else s.asset.attrs[ak] = av;
          if (before !== (av ?? undefined)) changes.push({ field: `attrs.${ak}`, before: before ?? null, after: av });
        }
        if (changes.some((c) => c.field.startsWith("attrs."))) changed.push("attrs");
        continue;
      }
      if (k === "zoneId") {
        const zone = ZONES.find((z) => z.id === v);
        if (v && !zone) throw new Error("that zone does not exist");
        if (s.zoneId !== v && zone) {
          changes.push({ field: "zone_id", before: s.asset.zoneCode, after: zone.code });
          s.zoneId = zone.id;
          s.asset.zoneCode = zone.code;
          s.asset.zoneName = zone.name;
          changed.push(k);
        }
        continue;
      }
      const key = k === "lifecycleStage" ? "stage" : k;
      if (a[key] !== v) {
        changes.push({ field: k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), before: a[key] ?? null, after: v });
        a[key] = v;
        changed.push(k);
      }
    }
    if (patch.parentId !== undefined)
      s.asset.parentTag = patch.parentId ? (this.assets.get(patch.parentId)?.asset.tag ?? null) : null;
    if (changes.length)
      this.logEvent(id, { at: new Date().toISOString(), kind: "changed", who, title: "asset.update", changes });
    this.refresh();
    return { changed };
  }

  /* ---- registering, history and notes ------------------------------------ */
  private log = new Map<string, AmHistoryEvent[]>();
  private notes = new Map<string, AmNote[]>();

  private logEvent(id: string, e: AmHistoryEvent) {
    this.log.set(id, [e, ...(this.log.get(id) ?? [])]);
  }

  types(): AmTypeDef[] {
    return TYPES.map((td) => ({
      key: td.key,
      name: td.name,
      category: td.category,
      geometryKind: td.key.includes("pavement") ? "polygon" : "point",
      conditionModel: null,
      schema: SAMPLE_SCHEMAS[td.key] ?? {},
    }));
  }

  create(input: AmCreateInput, who = "You"): { id: string; tag: string } {
    const td = TYPES.find((x) => x.key === input.assetType);
    if (!td) throw new Error(`asset type '${input.assetType}' is not installed`);
    if ([...this.assets.values()].some((x) => x.asset.tag === input.tag.trim()))
      throw new Error(`tag '${input.tag.trim()}' is already used on this site`);
    const zone = ZONES.find((z) => z.id === input.zoneId) ?? null;
    const id = `am-new-${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
    const parent = input.parentId ? this.assets.get(input.parentId) : undefined;
    const asset: AmAsset = {
      id,
      siteId: this.siteId,
      tag: input.tag.trim(),
      name: input.name.trim(),
      assetType: td.key,
      assetTypeName: td.name,
      category: td.category,
      conditionModel: null,
      zoneCode: zone?.code ?? null,
      zoneName: zone?.name ?? null,
      parentId: parent?.asset.id ?? null,
      parentTag: parent?.asset.tag ?? null,
      criticality: input.criticality,
      status: "in_service",
      stage: input.lifecycleStage,
      conditionScore: null,
      healthScore: null,
      healthBand: null,
      healthBasis: null,
      installedOn: input.installedOn ?? null,
      manufacturer: input.manufacturer ?? null,
      model: input.model ?? null,
      serialNo: input.serialNo ?? null,
      purchasedOn: input.purchasedOn ?? null,
      purchaseCost: input.purchaseCost ?? null,
      replacementValue: input.replacementValue ?? null,
      designLifeYears: input.designLifeYears ?? null,
      warrantyUntil: input.warrantyUntil ?? null,
      disposedOn: null,
      depreciationMethod: "straight_line",
      residualPct: 0,
      attrs: Object.fromEntries(Object.entries(input.attrs).filter(([, v]) => v !== null && v !== "")),
    };
    this.assets.set(id, { asset, zoneId: zone?.id ?? "", defects: [], prediction: null });
    this.workOrders.set(id, []);
    this.logEvent(id, { at: new Date().toISOString(), kind: "created", who, title: "asset.create", changes: [] });
    this.refresh();
    return { id, tag: asset.tag };
  }

  timeline(id: string): AmHistoryEvent[] {
    const s = this.assets.get(id);
    if (!s) throw new Error("asset not found");
    const events: AmHistoryEvent[] = [...(this.log.get(id) ?? [])];
    for (const w of this.workOrders.get(id) ?? []) {
      events.push({
        at: w.openedAt,
        kind: "work_order",
        who: null,
        title: `${w.number} · ${w.title}`,
        detail: { id: w.id, kind: w.kind, status: w.status },
      });
      if (w.completedAt)
        events.push({
          at: w.completedAt,
          kind: "work_done",
          who: null,
          title: `${w.number} · ${w.title}`,
          detail: { id: w.id, kind: w.kind, status: w.status },
        });
    }
    s.defects.forEach((sev, i) =>
      events.push({
        at: daysFrom(-10 - i * 7).toISOString(),
        kind: "defect",
        who: null,
        title: sev >= 4 ? "Serious finding awaiting repair" : "Minor finding recorded on inspection",
        detail: { severity: sev, status: "open" },
      }),
    );
    for (const n of this.notes.get(id) ?? [])
      events.push({ at: n.createdAt, kind: "note", who: n.createdBy, title: n.body, detail: { category: n.category } });
    return events.sort((a, b) => b.at.localeCompare(a.at));
  }

  listNotes(id: string): AmNote[] {
    return this.notes.get(id) ?? [];
  }

  addNote(id: string, body: string, category: NoteCategory, who = "You") {
    if (!this.assets.has(id)) throw new Error("asset not found");
    const note: AmNote = {
      id: `n-${Date.now()}`,
      category,
      body: body.trim(),
      createdAt: new Date().toISOString(),
      createdBy: who,
    };
    this.notes.set(id, [note, ...(this.notes.get(id) ?? [])]);
  }

  import(rows: AmImportRow[]): AmImportResult {
    let updated = 0;
    const notFound: string[] = [];
    for (const { tag, ...rest } of rows) {
      const s = [...this.assets.values()].find((x) => x.asset.tag === tag);
      if (!s) {
        notFound.push(tag);
        continue;
      }
      if (this.edit(s.asset.id, rest).changed.length) updated++;
    }
    return { updated, notFound, notFoundCount: notFound.length, errors: [] };
  }

  listPolicies(): AmPolicy[] {
    return [...this.policies.values()].sort((a, b) => a.assetType.localeCompare(b.assetType));
  }

  setPolicy(assetType: string, p: Omit<AmPolicy, "assetType">): AmPolicy {
    const row = { assetType, ...p };
    this.policies.set(assetType, row);
    this.refresh();
    return row;
  }

  condition(): AmCondition {
    const rows = [...this.assets.values()].filter((s) => !["disposed", "decommissioned"].includes(s.asset.stage));
    const matrix = Array.from({ length: 5 }, () => [0, 0, 0, 0, 0]);
    const bands: Record<string, number> = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
    const risks: AmCondition["topRisks"] = [];
    const byType = new Map<string, AmCondition["byType"][number] & { sum: number }>();
    for (const s of rows) {
      const a = s.asset;
      const t = byType.get(a.assetType) ?? {
        assetType: a.assetType,
        name: a.assetTypeName,
        assets: 0,
        assessed: 0,
        meanHealth: null,
        poor: 0,
        sum: 0,
      };
      byType.set(a.assetType, t);
      t.assets++;
      if (a.healthBand === null || a.healthScore === null) continue;
      matrix[a.criticality - 1][a.healthBand - 1]++;
      bands[String(a.healthBand)]++;
      t.assessed++;
      t.sum += a.healthScore;
      if (a.healthBand <= 2) t.poor++;
      risks.push({
        id: a.id,
        tag: a.tag,
        name: a.name,
        assetType: a.assetType,
        zoneCode: a.zoneCode,
        criticality: a.criticality,
        healthScore: a.healthScore,
        healthBand: a.healthBand,
        risk: riskOf(a.criticality, a.healthBand),
        basis: a.healthBasis,
      });
    }
    risks.sort((x, y) => y.risk - x.risk || x.healthScore - y.healthScore);
    const assessed = risks.length;
    const mean = assessed ? risks.reduce((s, r) => s + r.healthScore, 0) / assessed : 0;
    return {
      assets: rows.length,
      assessed,
      unassessed: rows.length - assessed,
      matrix,
      bands,
      topRisks: risks.slice(0, 25),
      highRisk: risks.filter((r) => r.risk >= 15).length,
      byType: [...byType.values()]
        .map(({ sum, ...t }) => ({ ...t, meanHealth: t.assessed ? Math.round((sum / t.assessed) * 10) / 10 : null }))
        .sort((a, b) => (a.meanHealth ?? 999) - (b.meanHealth ?? 999)),
      trend: Array.from({ length: 6 }, (_, i) => ({
        month: iso(new Date(Date.UTC(TODAY.getUTCFullYear(), TODAY.getUTCMonth() - (5 - i), 1))),
        meanHealth: Math.round((mean + (5 - i) * 0.9) * 10) / 10,
        assets: assessed,
      })),
      ...this.surveys(),
      method:
        "health = condition (or 100) − open defects (≤60) − age past 60% of design life (≤25) − predicted failure (≤30); risk = criticality × (6 − band)",
    };
  }

  maintenance(): AmMaintenance {
    const now = TODAY;
    const plans = this.plans.map((p) => ({ ...p, state: planState(p, now) }));
    const count = (st: AmPlan["state"]) => plans.filter((p) => p.state === st).length;
    const bad: AmMaintenance["badActors"] = [];
    let corrective = 0;
    let correctiveCost = 0;
    for (const [id, wos] of this.workOrders) {
      const done = wos.filter((w) => w.kind === "corrective");
      corrective += done.length;
      correctiveCost += done.reduce((s, w) => s + (w.costActual ?? 0), 0);
      if (!done.length) continue;
      const a = (this.assets.get(id) as Stored).asset;
      const hours = done.map(
        (w) => (new Date(w.completedAt as string).getTime() - new Date(w.openedAt).getTime()) / 3_600_000,
      );
      bad.push({
        id,
        tag: a.tag,
        name: a.name,
        assetType: a.assetType,
        criticality: a.criticality,
        failures: done.length,
        mtbfDays: Math.round((730 / done.length) * 10) / 10,
        mttrHours: Math.round((hours.reduce((s, h) => s + h, 0) / hours.length) * 10) / 10,
        cost: done.reduce((s, w) => s + (w.costActual ?? 0), 0),
      });
    }
    bad.sort((x, y) => y.failures - x.failures || y.cost - x.cost);
    const preventive = Math.round(plans.length * 2.4);
    const onTime = Math.round(preventive * 0.86);
    return {
      plans,
      summary: {
        plans: plans.length,
        active: plans.filter((p) => p.active).length,
        overdue: count("overdue"),
        due: count("due"),
        due30Days: plans.filter(
          (p) => p.active && p.nextDueAt && new Date(p.nextDueAt).getTime() <= now.getTime() + 30 * DAY,
        ).length,
        compliancePct: Math.round((onTime / (preventive + count("overdue"))) * 1000) / 10,
        pmCompleted12m: preventive,
        pmOnTime12m: onTime,
        plannedSharePct: Math.round((preventive / (preventive + corrective)) * 1000) / 10,
      },
      workMix12m: {
        preventive: { count: preventive, cost: preventive * 850 },
        corrective: { count: corrective, cost: correctiveCost },
      },
      badActors: bad.slice(0, 20),
      coverage: { workOrders24m: preventive + corrective, linkedToAsset: preventive + corrective },
      method:
        "compliance = PM work orders completed by their due date ÷ (completed + open past due), last 12 months; MTBF = 730 days ÷ corrective work orders on the asset; MTTR = mean started→completed hours",
    };
  }

  createPlan(input: AmPlanInput): AmPlan {
    const s = this.assets.get(input.assetId);
    if (!s) throw new Error("asset not found");
    const due = input.firstDueOn ? new Date(`${input.firstDueOn}T06:00:00Z`) : daysFrom(input.intervalDays ?? 30);
    const plan: StoredPlan = {
      id: `am-plan-${this.plans.length + 1}-${Date.now()}`,
      assetId: s.asset.id,
      tag: s.asset.tag,
      assetName: s.asset.name,
      assetType: s.asset.assetType,
      criticality: s.asset.criticality,
      title: input.title,
      trigger: input.trigger,
      intervalDays: input.intervalDays ?? null,
      usageMeasurement: input.usageMeasurement ?? null,
      intervalUsage: input.intervalUsage ?? null,
      leadDays: input.leadDays ?? 14,
      priority: input.priority ?? 3,
      estimatedHours: input.estimatedHours ?? null,
      estimatedCost: input.estimatedCost ?? null,
      tasks: input.tasks ?? [],
      active: true,
      lastDoneAt: null,
      nextDueAt: input.trigger === "calendar" ? due.toISOString() : null,
      state: "scheduled",
      openWorkOrder: null,
      createdAt: TODAY.toISOString(),
    };
    this.plans.push(plan);
    return { ...plan, state: planState(plan, TODAY) };
  }

  editPlan(id: string, patch: { active?: boolean; intervalDays?: number; nextDueOn?: string | null; title?: string }) {
    const p = this.plans.find((x) => x.id === id);
    if (!p) throw new DataSourceError("Not found: plan not found", { status: 404, detail: "plan not found" });
    /* build 6B: the platform's refusals (routers/asset_management.py `edit_plan`) */
    const why = planPatchRefusal(p.trigger, patch);
    if (why) throw new DataSourceError(`400: ${why}`, { status: 400, detail: why });
    if (patch.active !== undefined) p.active = patch.active;
    if (patch.intervalDays !== undefined) {
      p.intervalDays = patch.intervalDays;
      /* a new interval counts from the last completion, unless a date is given too */
      if (patch.nextDueOn === undefined && p.lastDoneAt)
        p.nextDueAt = new Date(new Date(p.lastDoneAt).getTime() + patch.intervalDays * DAY).toISOString();
    }
    if (patch.title !== undefined) p.title = patch.title;
    if (patch.nextDueOn !== undefined)
      p.nextDueAt = patch.nextDueOn ? new Date(`${patch.nextDueOn}T06:00:00Z`).toISOString() : null;
  }

  /* ── build 6B: a surveyed condition score, and the funding envelopes ── */

  recordCondition(id: string, input: AmConditionInput, who: string): AmConditionResult {
    const s = this.assets.get(id);
    if (!s) throw new DataSourceError("Not found: asset not found", { status: 404, detail: "asset not found" });
    const why = conditionRefusal(input, iso(new Date()));
    if (why) throw new DataSourceError(`400: ${why}`, { status: 400, detail: why });
    const before = s.asset.conditionScore;
    const at = new Date(`${input.surveyedOn || iso(new Date())}T12:00:00Z`).toISOString();
    s.asset.conditionScore = Math.round(input.score * 1000) / 1000;
    s.asset.conditionAt = at;
    s.asset.conditionBasis = input.basis.trim();
    s.asset.conditionBy = who;
    this.logEvent(id, {
      at: new Date().toISOString(),
      kind: "changed",
      who,
      title: "asset.condition_recorded",
      changes: [{ field: "condition_score", before, after: s.asset.conditionScore }],
    });
    this.refresh();
    return {
      id,
      tag: s.asset.tag,
      conditionScore: s.asset.conditionScore,
      conditionAt: at,
      conditionBasis: s.asset.conditionBasis,
      healthScore: s.asset.healthScore,
      healthBand: s.asset.healthBand,
    };
  }

  surveys(): { surveyed: number; recentSurveys: AmSurvey[] } {
    const scored = [...this.assets.values()].filter((s) => s.asset.conditionScore !== null);
    const recent = scored
      .filter((s) => s.asset.conditionAt)
      .sort((a, b) => (b.asset.conditionAt ?? "").localeCompare(a.asset.conditionAt ?? ""))
      .slice(0, 10)
      .map((s) => ({
        id: s.asset.id,
        tag: s.asset.tag,
        name: s.asset.name,
        conditionScore: s.asset.conditionScore as number,
        conditionAt: s.asset.conditionAt ?? null,
        conditionBasis: s.asset.conditionBasis ?? null,
        conditionBy: s.asset.conditionBy ?? null,
        healthScore: s.asset.healthScore,
        healthBand: s.asset.healthBand,
      }));
    return { surveyed: scored.length, recentSurveys: recent };
  }

  listEnvelopes(): FundingEnvelope[] {
    return [...this.envelopes].sort((a, b) => a.fiscalYear - b.fiscalYear || a.kind.localeCompare(b.kind));
  }

  setEnvelope(input: EnvelopeInput, who: string): void {
    const why = envelopeRefusal(input);
    if (why) throw new DataSourceError(`400: ${why}`, { status: 400, detail: why });
    const at = new Date().toISOString();
    const label = input.label?.trim() || null;
    const found = this.envelopes.find((e) => e.fiscalYear === input.fiscalYear && e.kind === input.kind);
    if (found) Object.assign(found, { amount: input.amount, currency: input.currency.trim().toUpperCase(), label, setBy: who, setAt: at });
    else
      this.envelopes.push({
        id: `env-${input.kind}-${input.fiscalYear}-${Date.now()}`,
        fiscalYear: input.fiscalYear,
        kind: input.kind,
        amount: input.amount,
        currency: input.currency.trim().toUpperCase(),
        label,
        scope: "site",
        applies: true,
        setBy: who,
        setAt: at,
      });
  }

  removeEnvelope(id: string): void {
    const i = this.envelopes.findIndex((e) => e.id === id);
    if (i < 0)
      throw new DataSourceError("Not found: that funding envelope does not exist", {
        status: 404,
        detail: "that funding envelope does not exist",
      });
    this.envelopes.splice(i, 1);
  }

  applyPlan(assetType: string, plan: Omit<AmPlanInput, "assetId">): { created: number } {
    let created = 0;
    for (const s of this.assets.values()) {
      if (s.asset.assetType !== assetType) continue;
      if (this.plans.some((p) => p.assetId === s.asset.id && p.title === plan.title)) continue;
      this.createPlan({ ...plan, assetId: s.asset.id });
      created++;
    }
    return { created };
  }

  generate(): { created: { planId: string; workOrder: string; asset: string }[] } {
    const created = [];
    for (const p of this.plans) {
      const st = planState(p, TODAY);
      if ((st === "due" || st === "overdue") && !p.openWorkOrder) {
        p.openWorkOrder = `WO-${String(++this.woSeq).padStart(6, "0")}`;
        created.push({ planId: p.id, workOrder: p.openWorkOrder, asset: p.tag ?? "" });
      }
    }
    return { created };
  }

  lifecycle(): AmLifecycle {
    const rows = [...this.assets.values()].filter((s) => s.asset.stage !== "disposed");
    const lives = rows.map((s) => ({ s, life: lifeOf(s.asset, this.policies.get(s.asset.assetType), TODAY) }));
    const forecast = renewalForecast(
      lives.map(({ s, life }) => ({ life, criticality: s.asset.criticality })),
      TODAY.getUTCFullYear(),
      10,
    ).map((b) => ({
      ...b,
      funded: this.envelopes.find((e) => e.kind === "capex" && e.fiscalYear === b.year)?.amount ?? null,
    }));
    const valued = lives.filter((l) => l.life.replacementValue !== null);
    const booked = lives.filter((l) => l.life.bookValue !== null);
    const stages: Record<string, number> = {};
    for (const s of rows) stages[s.asset.stage] = (stages[s.asset.stage] ?? 0) + 1;
    const expiring = lives
      .filter((l) => l.life.warrantyDaysLeft !== null && l.life.warrantyDaysLeft >= 0 && l.life.warrantyDaysLeft <= 90)
      .map((l) => ({
        id: l.s.asset.id,
        tag: l.s.asset.tag,
        name: l.s.asset.name,
        warrantyUntil: l.s.asset.warrantyUntil as string,
        daysLeft: l.life.warrantyDaysLeft as number,
      }))
      .sort((a, b) => a.daysLeft - b.daysLeft);
    return {
      coverage: {
        assets: rows.length,
        dated: lives.filter((l) => l.life.ageYears !== null).length,
        lifeKnown: lives.filter((l) => l.life.designLifeYears !== null).length,
        lifeFromPolicy: lives.filter((l) => l.life.lifeSource === "policy").length,
        valued: valued.length,
        valueFromPolicy: lives.filter((l) => l.life.valueSource === "policy").length,
        forecastable: lives.filter((l) => l.life.endOfLife !== null).length,
      },
      totals: {
        replacementValue: valued.reduce((s, l) => s + (l.life.replacementValue ?? 0), 0),
        bookValue: Math.round(booked.reduce((s, l) => s + (l.life.bookValue ?? 0), 0)),
        valuedAssets: valued.length,
        bookedAssets: booked.length,
        pastEndOfLife: lives.filter((l) => l.life.endOfLife && l.life.endOfLife < iso(TODAY)).length,
      },
      stages,
      forecast,
      warranties: {
        expiring90: expiring,
        expired: lives.filter((l) => l.life.warrantyDaysLeft !== null && l.life.warrantyDaysLeft < 0).length,
        inWarranty: lives.filter((l) => l.life.warrantyDaysLeft !== null && l.life.warrantyDaysLeft >= 0).length,
      },
      assets: lives
        .filter((l) => l.life.endOfLife !== null || l.life.replacementValue !== null)
        .sort(
          (a, b) =>
            (a.life.endOfLife ?? "9999").localeCompare(b.life.endOfLife ?? "9999") ||
            b.s.asset.criticality - a.s.asset.criticality,
        )
        .slice(0, 500)
        .map(({ s, life }) => ({
          ...life,
          id: s.asset.id,
          tag: s.asset.tag,
          name: s.asset.name,
          assetType: s.asset.assetType,
          assetTypeName: s.asset.assetTypeName,
          criticality: s.asset.criticality,
          stage: s.asset.stage,
          healthBand: s.asset.healthBand,
        })),
      policies: this.listPolicies(),
      method:
        "end of life = installed (or purchased) + design life; assets already past it are counted in this year; book value by each asset's depreciation method; figures marked 'policy' come from the asset-type policy, not the asset",
    };
  }
}
